import { RecordingPlayer } from "@/components/calling/RecordingPlayer";
import { CallAiActions } from "@/components/ai/CallAiActions";
import { RecordProblem } from "@/components/patterns";
import { DealRecordHeader } from "./record/DealRecordHeader";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { DealRelated } from "./record/DealRelated";
import { DealComposer } from "./record/DealComposer";
import type { DealSummary } from "./record/types";
import { usePins } from "@/lib/usePins";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject, unwrapArray } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { InfiniteScroll } from "@/components/ui/infinite-scroll";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Can } from "@/components/auth/Can";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Building,
  User,
  Mail,
  Phone,
  PhoneCall,
  MessageSquare,
  Calendar,
  Paperclip,
  StickyNote,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  DollarSign,
  IndianRupee,
  Tag,
  Trash2,
  Edit,
  Send,
  Plus,
  Loader2,
  History,
  FileCheck,
  Globe,
  MapPin,
  Briefcase,
  ArrowRight,
  ArrowRightLeft,
  MessageCircle,
  Sparkles,
  Search,
  UploadCloud,
  Link2,
  FileText,
  X,
  Video,
  Users,
  Flame,
  PhoneIncoming,
  PhoneOutgoing,
  Check,
  ChevronDown,
  LayoutGrid,
} from "lucide-react";
import { formatINR } from "@/lib/utils";
import { DealProductsCard } from "./DealProductsCard";
import { DealQuotesCard } from "./DealQuotesCard";
import { DealOrdersCard } from "./DealOrdersCard";
import { SamplesCard } from "@/pages/samples/SamplesCard";
import { DealProposalsCard } from "./DealProposalsCard";
import { DealAgreementsCard } from "./DealAgreementsCard";
import { DealGatesProvider, DealMoveCard, DealQualificationTab } from "./record/DealGates";
import { DealNextStep } from "./record/DealNextStep";
import { VISIT_CHANGED } from "@/lib/visits";
import { VisitTimelineExtra } from "@/components/visits/VisitTimelineExtra";
import { DealTabs, type DealSection } from "./record/DealTabs";
import { StageMoveDialog, type StageMoveRequest } from "@/components/deals/StageMoveDialog";
import { DealTasksCard } from "./DealTasksCard";
import { DealHealthCard } from "@/components/ai/DealHealthCard";
import { ComposeEmailDialog, type ComposeDefaults } from "./ComposeEmailDialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { EmailThreadItem, groupEmailThreads, replyDefaults, type EmailThread } from "./DealEmailThread";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WhatsAppTimelineItem, type WhatsAppThread } from "@/components/whatsapp/WhatsAppTimelineItem";
import type { DealEmailRecord, MailboxInfo } from "@/types/email";
import type {
  DealRecord,
  PipelineStageRecord,
  DealActivityRecord,
  DealStageHistoryRecord,
  DealChangelogRecord,
  MasterLookupRecord,
} from "@/types";
import { dateLocale, withClock } from "@/lib/dateTime";

/** Must match DEAL_FILE_TYPES in backend/src/Helper/DealFile.helper.ts */
const ALLOWED_FILE_TYPES =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.zip";

/** Files uploaded to the deal (new private storage, or the old /uploads folder) */
const isUploadedFile = (url?: string) => !!url && (url.startsWith("deal-files/") || url.startsWith("/uploads/"));

/** Uploaded files open through the login- and scope-checked endpoint; links only when http(s) */
const fileHref = (act: { id: string; file_url?: string }) => {
  if (isUploadedFile(act.file_url)) return `/api/DealActivity/File/${act.id}`;
  return act.file_url && /^https?:\/\//i.test(act.file_url) ? act.file_url : null;
};

type ActivityTab =
  | "ALL"
  | "PLANNED"
  | "NOTE"
  | "CALL"
  | "MEETING"
  | "WHATSAPP"
  | "FILE"
  | "EMAIL"
  | "CHANGELOG";

export function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();

  const [deal, setDeal] = useState<DealRecord | null>(null);
  useRecordCrumb(deal?.name);
  const [summary, setSummary] = useState<DealSummary | null>(null);
  const pins = usePins();
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskVersion, setTaskVersion] = useState(0);
  const [activities, setActivities] = useState<DealActivityRecord[]>([]);
  const [changelogs, setChangelogs] = useState<DealChangelogRecord[]>([]);
  const [activityMeta, setActivityMeta] = useState({ page: 1, total: 0, pageSize: 30, loading: false });
  const [emailMeta, setEmailMeta] = useState({ page: 1, total: 0, pageSize: 30, loading: false });
  const [changelogMeta, setChangelogMeta] = useState({ page: 1, total: 0, pageSize: 30, loading: false });
  const [changelogFilter, setChangelogFilter] = useState<"ALL" | "STAGE" | "DATA" | "ACTIVITY">("ALL");
  const [changelogSearch, setChangelogSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  // When a deal has product lines its amount is derived from them and not edited by hand
  const [lineItemCount, setLineItemCount] = useState(0);
  // Which section of the deal is open, and (inside Activity) which kind of activity is shown
  const [section, setSection] = useState<DealSection>("activity");
  const [activityFilter, setActivityFilter] = useState<ActivityTab>("ALL");
  const activeTab: ActivityTab = section === "files" ? "FILE" : section === "history" ? "CHANGELOG" : activityFilter;

  // Emails: shown as conversations in the activity timeline, sent from the Mail quick action
  const canReadEmails = hasPermission("email.read");
  const canSendEmails = hasPermission("email.send");
  const [emails, setEmails] = useState<DealEmailRecord[]>([]);
  const [mailbox, setMailbox] = useState<MailboxInfo | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDefaults, setComposeDefaults] = useState<ComposeDefaults>({ to: "", subject: "" });

  // WhatsApp: chats linked to this deal show in the timeline; the WhatsApp button opens the chat popup
  const canReadWhatsApp = hasPermission("whatsapp.read");
  const canSendWhatsApp = hasPermission("whatsapp.send");
  const [waThreads, setWaThreads] = useState<WhatsAppThread[]>([]);
  const [waChatOpen, setWaChatOpen] = useState(false);

  // Modals
  const [editOpen, setEditOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [wonOpen, setWonOpen] = useState(false);
  const [stageMoveRequest, setStageMoveRequest] = useState<StageMoveRequest>(null);
  const [wonGate, setWonGate] = useState<{ allowed: boolean; missing: string[]; can_override: boolean } | null>(null);
  const [wonReason, setWonReason] = useState("");
  const [lostOpen, setLostOpen] = useState(false);

  // Forms
  const [noteText, setNoteText] = useState("");
  const [noteHtml, setNoteHtml] = useState("");
  const [callForm, setCallForm] = useState({
    title: "Phone Call",
    direction: "OUTBOUND",
    outcome: "Connected",
    duration: 5,
    sentiment: "WARM",
    next_followup_date: "",
    notes: "",
  });
  const [meetingForm, setMeetingForm] = useState({
    title: "Client Demo / Discussion",
    meeting_mode: "GOOGLE_MEET",
    meeting_link: "",
    scheduled_at: "",
    duration: 30,
    meeting_status: "SCHEDULED",
    attendees: "",
    next_action: "",
    notes: "",
  });
  const [meetingAttendeesOptions, setMeetingAttendeesOptions] = useState<{
    id: string;
    name: string;
    email?: string;
    role?: string;
    type: "contact" | "team";
  }[]>([]);
  const [attendeesDropdownOpen, setAttendeesDropdownOpen] = useState(false);
  const [attendeesSearch, setAttendeesSearch] = useState("");
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const attendeesDropdownRef = React.useRef<HTMLDivElement>(null);
  const [entityTab, setEntityTab] = useState<"deal" | "account" | "contact" | "all">("deal");
  const [fileMode, setFileMode] = useState<"upload" | "url">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileForm, setFileForm] = useState({
    file_name: "",
    file_url: "",
    notes: "",
  });
  const [lostReasons, setLostReasons] = useState<MasterLookupRecord[]>([]);
  const [lostForm, setLostForm] = useState({
    lost_reason: "",
    lost_notes: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    amount: 0,
    expected_close_date: "",
    account_id: "",
    primary_contact_id: "",
  });

  // Edit Company Account Dialog State
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    name: "",
    industry: "",
    website: "",
    phone: "",
    email: "",
    billing_address: "",
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [accountEditMode, setAccountEditMode] = useState<"edit_current" | "switch_account" | "create_new">("edit_current");

  // Edit Primary Contact Dialog State
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    job_title: "",
    email: "",
    phone: "",
    mobile: "",
  });
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [contactEditMode, setContactEditMode] = useState<"edit_current" | "switch_contact" | "create_new">("edit_current");

  // Right side Activity Tabs scrolling & visibility
  const activityTabsRef = React.useRef<HTMLDivElement>(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const checkTabsScroll = React.useCallback(() => {
    const el = activityTabsRef.current;
    if (!el) return;
    setCanScrollTabsLeft(el.scrollLeft > 4);
    setCanScrollTabsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollTabs = (direction: "left" | "right") => {
    const el = activityTabsRef.current;
    if (!el) return;
    const offset = direction === "left" ? -160 : 160;
    el.scrollBy({ left: offset, behavior: "smooth" });
    setTimeout(checkTabsScroll, 200);
  };

  useEffect(() => {
    checkTabsScroll();
    window.addEventListener("resize", checkTabsScroll);
    return () => window.removeEventListener("resize", checkTabsScroll);
  }, [checkTabsScroll, activities, emails, changelogs]);

  useEffect(() => {
    const el = activityTabsRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(`[data-tab="${activeTab}"]`) as HTMLElement;
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
    setTimeout(checkTabsScroll, 250);
  }, [activeTab, checkTabsScroll]);

  // Load Master Lookup values from API
  useEffect(() => {
    Promise.all([
      api.get("/api/MasterLookup/ByCategory/DEAL_LOST_REASON").catch(() => ({ Data: [] })),
    ]).then(([lostRes]) => {
      setLostReasons(unwrapArray<MasterLookupRecord>(lostRes));
    }).catch(() => {});
  }, []);

  // Load Deal, Activities and Changelogs
  const [problem, setProblem] = useState<unknown>(null);
  const loadDealData = async () => {
    if (!id) return;
    try {
      setProblem(null);
      const [dealRes, actRes, changelogRes, emailRes, waRes] = await Promise.all([
        api.get(`/api/Deal/ById/${id}`),
        api.get(`/api/DealActivity/ListByDeal/${id}?page=1`).catch(() => ({ data: { rows: [] } })),
        api.get(`/api/DealChangelog/ListByDeal/${id}?page=1`).catch(() => ({ data: { rows: [] } })),
        canReadEmails ? api.get(`/api/DealEmail/ByDeal/${id}?page=1`).catch(() => ({ data: { rows: [] } })) : Promise.resolve({ data: { rows: [] } }),
        canReadWhatsApp ? api.get(`/api/WhatsApp/ByDeal/${id}`).catch(() => []) : Promise.resolve([]),
      ]);
      const emailPage = unwrapPage<DealEmailRecord>(emailRes);
      setEmails(emailPage.rows);
      setEmailMeta({ page: emailPage.page, total: emailPage.total, pageSize: emailPage.page_size, loading: false });
      setWaThreads(unwrapArray<WhatsAppThread>(waRes));

      const dealData = unwrapObject<DealRecord>(dealRes);
      setDeal(dealData);
      // Header data (days per stage, account figures, BANT lines); the page works without it
      api.get(`/api/Deal/Summary/${id}`).then((r) => setSummary(unwrapObject<DealSummary>(r))).catch(() => setSummary(null));

      const actPage = unwrapPage<DealActivityRecord>(actRes);
      setActivities(actPage.rows);
      setActivityMeta({ page: actPage.page, total: actPage.total, pageSize: actPage.page_size, loading: false });

      const changelogPage = unwrapPage<DealChangelogRecord>(changelogRes);
      setChangelogs(changelogPage.rows);
      setChangelogMeta({ page: changelogPage.page, total: changelogPage.total, pageSize: changelogPage.page_size, loading: false });

      if (dealData) {
        let expectedCloseStr = "";
        if (dealData.expected_close_date) {
          const parsed = new Date(dealData.expected_close_date);
          if (!isNaN(parsed.getTime())) {
            expectedCloseStr = parsed.toISOString().slice(0, 10);
          }
        }
        setEditForm({
          name: dealData.name || "",
          amount: Number(dealData.amount || 0),
          expected_close_date: expectedCloseStr,
          account_id: dealData.account_id || "",
          primary_contact_id: dealData.primary_contact_id || "",
        });
      }
    } catch (err: any) {
      setProblem(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDealData();
  }, [id]);

  // A call just saved from the softphone: show it without a page reload
  const reloadDealRef = React.useRef(loadDealData);
  reloadDealRef.current = loadDealData;
  useEffect(() => {
    const onSaved = () => void reloadDealRef.current();
    window.addEventListener("crm:call-saved", onSaved);
    // A visit for this deal was checked out: its timeline and next step change
    window.addEventListener(VISIT_CHANGED, onSaved);
    return () => {
      window.removeEventListener("crm:call-saved", onSaved);
      window.removeEventListener(VISIT_CHANGED, onSaved);
    };
  }, []);

  const feedHasMore = (meta: { page: number; pageSize: number; total: number }) => meta.page * meta.pageSize < meta.total;

  const loadMoreTimeline = async () => {
    if (!id) return;
    const wantEmail = activeTab === "ALL" || activeTab === "EMAIL";
    const wantActivity = activeTab !== "EMAIL" && activeTab !== "WHATSAPP";
    if (wantActivity && feedHasMore(activityMeta) && !activityMeta.loading) {
      setActivityMeta((m) => ({ ...m, loading: true }));
      try {
        const paged = unwrapPage<DealActivityRecord>(await api.get(`/api/DealActivity/ListByDeal/${id}?page=${activityMeta.page + 1}`));
        setActivities((prev) => {
          const ids = new Set(prev.map((row) => row.id));
          return [...prev, ...paged.rows.filter((row) => !ids.has(row.id))];
        });
        setActivityMeta({ page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false });
      } catch {
        setActivityMeta((m) => ({ ...m, loading: false }));
      }
      return;
    }
    if (wantEmail && feedHasMore(emailMeta) && !emailMeta.loading) {
      setEmailMeta((m) => ({ ...m, loading: true }));
      try {
        const paged = unwrapPage<DealEmailRecord>(await api.get(`/api/DealEmail/ByDeal/${id}?page=${emailMeta.page + 1}`));
        setEmails((prev) => {
          const ids = new Set(prev.map((row) => row.id));
          return [...prev, ...paged.rows.filter((row) => !ids.has(row.id))];
        });
        setEmailMeta({ page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false });
      } catch {
        setEmailMeta((m) => ({ ...m, loading: false }));
      }
    }
  };

  const loadMoreChangelog = async () => {
    if (!id || changelogMeta.loading || !feedHasMore(changelogMeta)) return;
    setChangelogMeta((m) => ({ ...m, loading: true }));
    try {
      const paged = unwrapPage<DealChangelogRecord>(await api.get(`/api/DealChangelog/ListByDeal/${id}?page=${changelogMeta.page + 1}`));
      setChangelogs((prev) => {
        const ids = new Set(prev.map((row) => row.id));
        return [...prev, ...paged.rows.filter((row) => !ids.has(row.id))];
      });
      setChangelogMeta({ page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false });
    } catch {
      setChangelogMeta((m) => ({ ...m, loading: false }));
    }
  };

  const timelineHasMore = (() => {
    const wantEmail = activeTab === "ALL" || activeTab === "EMAIL";
    const wantActivity = activeTab !== "EMAIL" && activeTab !== "WHATSAPP";
    return (wantActivity && feedHasMore(activityMeta)) || (wantEmail && feedHasMore(emailMeta));
  })();
  const timelineLoading = activityMeta.loading || emailMeta.loading;

  useEffect(() => {
    if (!canSendEmails) return;
    api
      .get("/api/Mailbox/Me")
      .then((res) => setMailbox(unwrapObject<{ mailbox: MailboxInfo | null }>(res)?.mailbox || null))
      .catch(() => setMailbox(null));
  }, [canSendEmails]);

  // Sorted stages for the chevron bar
  const pipelineStages = useMemo(() => {
    if (!deal?.pipeline?.stages) return [];
    return [...deal.pipeline.stages].sort((a, b) => a.order_index - b.order_index);
  }, [deal]);

  // Current stage index
  const currentStageIndex = useMemo(() => {
    if (!deal || !pipelineStages.length) return -1;
    return pipelineStages.findIndex((s) => s.id === deal.stage_id);
  }, [deal, pipelineStages]);

  // Move stage via clicking on chevron
  const handleStageClick = async (stage: PipelineStageRecord) => {
    if (!deal || deal.stage_id === stage.id || actionLoading) return;

    if (stage.is_won_stage) {
      openWonDialog();
      return;
    }
    if (stage.is_lost_stage) {
      setLostOpen(true);
      return;
    }

    // Reopen closed deal check
    const isCurrentlyClosed = deal.deal_status === "WON" || deal.deal_status === "LOST";
    const willBeOpen = !stage.is_won_stage && !stage.is_lost_stage;
    if (isCurrentlyClosed && willBeOpen && !hasPermission("deal.reopen")) {
      error("Permission Denied", "You do not have permission ('deal.reopen') to reopen closed deals.");
      return;
    }

    // Gated move: straight through when allowed, otherwise the dialog explains what's missing
    setStageMoveRequest({ dealId: deal.id, dealName: deal.name, stage: { id: stage.id, name: stage.name } });
  };

  /** Won checks the gate first (agreement signed / PO received); a manager may override with a reason */
  const sendQuote = async () => {
    if (!deal) return;
    setActionLoading(true);
    try {
      const res: any = await api.post("/api/Quotation/Insert", { deal_id: deal.id });
      success("Quote created", "Draft quote created from this deal's products");
      navigate(`/quotes/${res?.AddtionalData}`);
    } catch (err: any) {
      error("Could not create the quote", err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const openWonDialog = () => {
    if (!deal) return;
    setWonGate(null);
    setWonReason("");
    setWonOpen(true);
    const wonStage = deal.pipeline?.stages?.find((s) => s.is_won_stage);
    if (wonStage) {
      api
        .get(`/api/Deal/Gates/${deal.id}?target=${wonStage.id}`)
        .then((res: any) => setWonGate({ allowed: !!res?.move?.allowed, missing: res?.move?.missing || [], can_override: !!res?.can_override }))
        .catch(() => undefined);
    }
  };

  // Mark Won
  const handleMarkWon = async () => {
    if (!deal) return;
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkWon/${deal.id}`, {
        won_amount: Number(deal.amount),
        actual_close_date: new Date(),
        ...(wonGate && !wonGate.allowed ? { override: true, reason: wonReason.trim() } : {}),
      });
      success("Deal Won!", "Congratulations on closing this deal!");
      setWonOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Action Failed", err.message || "Could not mark as won");
    } finally {
      setActionLoading(false);
    }
  };

  // Mark Lost
  const handleMarkLost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkLost/${deal.id}`, {
        lost_reason: lostForm.lost_reason,
        lost_notes: lostForm.lost_notes,
      });
      success("Deal Marked as Lost", "Stage and status updated");
      setLostOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Action Failed", err.message || "Could not mark as lost");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit Deal
  const openEditDeal = () => {
    if (!deal) return;
    setEditOpen(true);
  };

  // Update Deal Info
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      await api.put(`/api/Deal/Update/${deal.id}`, {
        name: editForm.name,
        amount: Number(editForm.amount),
        account_id: editForm.account_id ? editForm.account_id : null,
        pipeline_id: deal.pipeline_id,
        stage_id: deal.stage_id,
        primary_contact_id: editForm.primary_contact_id ? editForm.primary_contact_id : null,
        expected_close_date: editForm.expected_close_date
          ? new Date(editForm.expected_close_date)
          : undefined,
      });
      success("Deal Updated", "Deal metadata saved successfully");
      setEditOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Update Failed", err.message || "Could not update deal");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit Company Account Dialog
  const openEditAccount = () => {
    if (!deal) return;
    if (deal.account) {
      setAccountForm({
        name: deal.account.name || "",
        industry: deal.account.industry || "",
        website: deal.account.website || "",
        phone: deal.account.phone || "",
        email: deal.account.email || "",
        billing_address: deal.account.billing_address || "",
      });
      setSelectedAccountId(deal.account_id || "");
      setAccountEditMode("edit_current");
    } else {
      setAccountForm({
        name: "",
        industry: "",
        website: "",
        phone: "",
        email: "",
        billing_address: "",
      });
      setSelectedAccountId("");
      setAccountEditMode("switch_account");
    }
    setEditAccountOpen(true);
  };

  // Handle Submit Company Account
  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      if (accountEditMode === "edit_current") {
        if (!deal.account_id) {
          error("Error", "No company account linked to edit");
          return;
        }
        if (!accountForm.name.trim()) {
          error("Validation", "Company name is required");
          return;
        }
        await api.put(`/api/Account/Update/${deal.account_id}`, {
          name: accountForm.name.trim(),
          industry: accountForm.industry.trim() || undefined,
          website: accountForm.website.trim() || undefined,
          phone: accountForm.phone.trim() || undefined,
          email: accountForm.email.trim() || undefined,
          billing_address: accountForm.billing_address.trim() || undefined,
        });
        success("Company Updated", "Company account details saved successfully");
      } else if (accountEditMode === "switch_account") {
        if (!selectedAccountId) {
          error("Validation", "Please select a company account");
          return;
        }
        await api.put(`/api/Deal/Update/${deal.id}`, {
          account_id: selectedAccountId,
        });
        success("Company Linked", "Deal linked to selected company account");
      } else if (accountEditMode === "create_new") {
        if (!accountForm.name.trim()) {
          error("Validation", "Company name is required");
          return;
        }
        const createRes: any = await api.post("/api/Account/Insert", {
          name: accountForm.name.trim(),
          industry: accountForm.industry.trim() || undefined,
          website: accountForm.website.trim() || undefined,
          phone: accountForm.phone.trim() || undefined,
          email: accountForm.email.trim() || undefined,
          billing_address: accountForm.billing_address.trim() || undefined,
        });
        const createdId = createRes?.AddtionalData || createRes?.data?.id || createRes?.id;
        if (createdId) {
          await api.put(`/api/Deal/Update/${deal.id}`, {
            account_id: createdId,
          });
        }
        success("Company Created", "New company created and linked to deal");
      }
      setEditAccountOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update company account");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit Primary Contact Dialog
  const openEditContact = () => {
    if (!deal) return;
    if (deal.primary_contact) {
      setContactForm({
        first_name: deal.primary_contact.first_name || "",
        last_name: deal.primary_contact.last_name || "",
        job_title: deal.primary_contact.job_title || "",
        email: deal.primary_contact.email || "",
        phone: deal.primary_contact.phone || "",
        mobile: deal.primary_contact.mobile || "",
      });
      setSelectedContactId(deal.primary_contact_id || "");
      setContactEditMode("edit_current");
    } else {
      setContactForm({
        first_name: "",
        last_name: "",
        job_title: "",
        email: "",
        phone: "",
        mobile: "",
      });
      setSelectedContactId("");
      setContactEditMode("switch_contact");
    }
    setEditContactOpen(true);
  };

  // Handle Submit Primary Contact
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      if (contactEditMode === "edit_current") {
        if (!deal.primary_contact_id) {
          error("Error", "No primary contact linked to edit");
          return;
        }
        if (!contactForm.first_name.trim()) {
          error("Validation", "First name is required");
          return;
        }
        await api.put(`/api/Contact/Update/${deal.primary_contact_id}`, {
          first_name: contactForm.first_name.trim(),
          last_name: contactForm.last_name.trim() || undefined,
          job_title: contactForm.job_title.trim() || undefined,
          email: contactForm.email.trim() || undefined,
          phone: contactForm.phone.trim() || undefined,
          mobile: contactForm.mobile.trim() || undefined,
        });
        success("Contact Updated", "Primary contact details updated successfully");
      } else if (contactEditMode === "switch_contact") {
        if (!selectedContactId) {
          error("Validation", "Please select a contact person");
          return;
        }
        await api.put(`/api/Deal/Update/${deal.id}`, {
          primary_contact_id: selectedContactId,
        });
        success("Contact Linked", "Deal primary contact updated");
      } else if (contactEditMode === "create_new") {
        if (!contactForm.first_name.trim()) {
          error("Validation", "First name is required");
          return;
        }
        const createRes: any = await api.post("/api/Contact/Insert", {
          account_id: deal.account_id || undefined,
          first_name: contactForm.first_name.trim(),
          last_name: contactForm.last_name.trim() || undefined,
          job_title: contactForm.job_title.trim() || undefined,
          email: contactForm.email.trim() || undefined,
          phone: contactForm.phone.trim() || undefined,
          mobile: contactForm.mobile.trim() || undefined,
          is_primary: true,
        });
        const createdId = createRes?.AddtionalData || createRes?.data?.id || createRes?.id;
        if (createdId) {
          await api.put(`/api/Deal/Update/${deal.id}`, {
            primary_contact_id: createdId,
          });
        }
        success("Contact Created", "New contact created and linked to deal");
      }
      setEditContactOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update contact");
    } finally {
      setActionLoading(false);
    }
  };

  // Add Note Activity
  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal || !noteText.trim()) return;
    setActionLoading(true);
    try {
      await api.post("/api/DealActivity/Insert", {
        deal_id: deal.id,
        activity_type: "NOTE",
        title: "Added a Note",
        description: noteText.trim(),
        description_html: noteHtml,
      });
      success("Note Saved", "Note added to activity timeline");
      setNoteText("");
      setNoteHtml("");
      setNoteOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Failed to add note", err.message || "Could not save note");
    } finally {
      setActionLoading(false);
    }
  };

  // Log Call Activity
  const handleLogCall = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      await api.post("/api/DealActivity/Insert", {
        deal_id: deal.id,
        activity_type: "CALL",
        title: callForm.title || "Phone Call",
        call_direction: callForm.direction,
        call_outcome: callForm.outcome,
        call_duration: Number(callForm.duration) || 0,
        sentiment: callForm.sentiment,
        next_followup_date: callForm.next_followup_date ? new Date(callForm.next_followup_date) : undefined,
        description: callForm.notes || undefined,
      });
      success("Call Logged", "Call record added to timeline");
      setCallForm({
        title: "Phone Call",
        direction: "OUTBOUND",
        outcome: "Connected",
        duration: 5,
        sentiment: "WARM",
        next_followup_date: "",
        notes: "",
      });
      setCallOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Failed to log call", err.message || "Could not save call log");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle click outside attendees dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        attendeesDropdownRef.current &&
        !attendeesDropdownRef.current.contains(event.target as Node)
      ) {
        setAttendeesDropdownOpen(false);
      }
    };
    if (attendeesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [attendeesDropdownOpen]);

  const loadMeetingAttendees = async (search: string) => {
    try {
      const qs = new URLSearchParams({ page: "1", page_size: "20" });
      if (search.trim()) qs.set("search", search.trim());
      const [usersRes, contactsRes] = await Promise.all([
        api.get("/api/User/Assignable").catch(() => ({ Data: [] })),
        api.get(`/api/Contact/List?${qs.toString()}`).catch(() => ({ data: { rows: [] } })),
      ]);
      const usersList: any[] = unwrapArray(usersRes);
      const contactsList: any[] = unwrapPage(contactsRes).rows;

      const options: {
        id: string;
        name: string;
        email?: string;
        role?: string;
        type: "contact" | "team";
      }[] = [];

      // Primary contact first
      if (deal?.primary_contact) {
        options.push({
          id: `contact-primary-${deal.primary_contact.id}`,
          name: `${deal.primary_contact.first_name || ""} ${deal.primary_contact.last_name || ""}`.trim() || deal.primary_contact.email || "Primary Contact",
          email: deal.primary_contact.email || "",
          role: `${deal.primary_contact.job_title || "Contact"} (Primary)`,
          type: "contact",
        });
      }

      // Other contacts
      contactsList.forEach((c) => {
        if (deal?.primary_contact && c.id === deal.primary_contact.id) return;
        const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Contact";
        options.push({
          id: `contact-${c.id}`,
          name,
          email: c.email || "",
          role: c.job_title || "Client Contact",
          type: "contact",
        });
      });

      // Internal team
      usersList.forEach((u) => {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "Team Member";
        options.push({
          id: `user-${u.id}`,
          name,
          email: u.email || "",
          role: u.role || u.user_role?.name || "Team Member",
          type: "team",
        });
      });

      const q = search.trim().toLowerCase();
      setMeetingAttendeesOptions(
        q
          ? options.filter(
              (opt) =>
                opt.name.toLowerCase().includes(q) ||
                (opt.email && opt.email.toLowerCase().includes(q)) ||
                (opt.role && opt.role.toLowerCase().includes(q)),
            )
          : options,
      );
    } catch (e) {
      console.error("Failed to load attendees options", e);
    }
  };

  // Open Meeting Modal & Load Attendees
  const openMeetingModal = () => {
    setMeetingOpen(true);
    setAttendeesDropdownOpen(false);
    setAttendeesSearch("");
    if (selectedAttendees.length === 0 && deal?.primary_contact) {
      const pName = `${deal.primary_contact.first_name || ""} ${deal.primary_contact.last_name || ""}`.trim();
      if (pName) {
        setSelectedAttendees([pName]);
        setMeetingForm((prev) => ({ ...prev, attendees: pName }));
      }
    }
  };

  useEffect(() => {
    if (!meetingOpen) return;
    const timer = setTimeout(() => loadMeetingAttendees(attendeesSearch), 250);
    return () => clearTimeout(timer);
  }, [meetingOpen, attendeesSearch]);

  const handleUpdateAttendees = (newSelected: string[]) => {
    setSelectedAttendees(newSelected);
    setMeetingForm((prev) => ({
      ...prev,
      attendees: newSelected.join(", "),
    }));
  };

  const handleToggleAttendee = (attendeeName: string) => {
    if (selectedAttendees.includes(attendeeName)) {
      handleUpdateAttendees(selectedAttendees.filter((a) => a !== attendeeName));
    } else {
      handleUpdateAttendees([...selectedAttendees, attendeeName]);
    }
  };

  const handleRemoveAttendee = (attendeeName: string) => {
    handleUpdateAttendees(selectedAttendees.filter((a) => a !== attendeeName));
  };

  const filteredAttendeeOptions = useMemo(() => {
    if (!attendeesSearch.trim()) return meetingAttendeesOptions;
    const q = attendeesSearch.toLowerCase();
    return meetingAttendeesOptions.filter(
      (opt) =>
        opt.name.toLowerCase().includes(q) ||
        (opt.email && opt.email.toLowerCase().includes(q)) ||
        (opt.role && opt.role.toLowerCase().includes(q))
    );
  }, [meetingAttendeesOptions, attendeesSearch]);

  const handleSelectAllFiltered = () => {
    const allFilteredNames = filteredAttendeeOptions.map((o) => o.name);
    const combined = Array.from(new Set([...selectedAttendees, ...allFilteredNames]));
    handleUpdateAttendees(combined);
  };

  // Schedule Meeting Activity
  const handleScheduleMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      await api.post("/api/DealActivity/Insert", {
        deal_id: deal.id,
        activity_type: "MEETING",
        title: meetingForm.title || "Meeting Scheduled",
        scheduled_at: meetingForm.scheduled_at ? new Date(meetingForm.scheduled_at) : new Date(),
        meeting_mode: meetingForm.meeting_mode,
        meeting_link: meetingForm.meeting_link?.trim() || undefined,
        meeting_duration: Number(meetingForm.duration) || 30,
        meeting_status: meetingForm.meeting_status,
        attendees: meetingForm.attendees?.trim() || undefined,
        next_action: meetingForm.next_action?.trim() || undefined,
        description: meetingForm.notes || undefined,
      });
      success("Meeting Saved", "Meeting added to timeline");
      setMeetingForm({
        title: "Client Demo / Discussion",
        meeting_mode: "GOOGLE_MEET",
        meeting_link: "",
        scheduled_at: "",
        duration: 30,
        meeting_status: "SCHEDULED",
        attendees: "",
        next_action: "",
        notes: "",
      });
      setSelectedAttendees([]);
      setAttendeesDropdownOpen(false);
      setAttendeesSearch("");
      setMeetingOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Failed to schedule meeting", err.message || "Could not schedule meeting");
    } finally {
      setActionLoading(false);
    }
  };

  // Open File Modal
  const openFileModal = () => {
    setFileMode("upload");
    setSelectedFile(null);
    setFileForm({ file_name: "", file_url: "", notes: "" });
    setFileOpen(true);
  };

  // Attach File Activity (Upload or External URL)
  const handleAttachFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return;
    setActionLoading(true);
    try {
      let finalUrl = fileForm.file_url.trim();
      let finalName = fileForm.file_name.trim();

      if (fileMode === "upload") {
        if (!selectedFile) {
          error("Validation", "Please select a file to upload");
          setActionLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append("deal_id", deal.id);
        formData.append("file", selectedFile);
        const uploadRes: any = await api.upload("/api/Upload/File", formData);
        const uploadedData = uploadRes?.AddtionalData || uploadRes?.data || uploadRes;
        finalUrl = uploadedData?.url;
        if (!finalName) {
          finalName = uploadedData?.original_name || selectedFile.name;
        }
      } else {
        if (!finalUrl) {
          error("Validation", "Paste the link to the file");
          setActionLoading(false);
          return;
        }
        if (!/^https?:\/\//i.test(finalUrl)) {
          error("Validation", "That doesn't look like a full link. Copy it again from the address bar and paste it here.");
          setActionLoading(false);
          return;
        }
        if (!finalName) {
          finalName = "Document Link";
        }
      }

      await api.post("/api/DealActivity/Insert", {
        deal_id: deal.id,
        activity_type: "FILE",
        title: `Attached Document: ${finalName}`,
        file_name: finalName,
        file_url: finalUrl || "#",
        description: fileForm.notes.trim() || undefined,
      });

      success("Document Attached", `Successfully attached "${finalName}" to deal.`);
      setFileForm({ file_name: "", file_url: "", notes: "" });
      setSelectedFile(null);
      setFileOpen(false);
      await loadDealData();
    } catch (err: any) {
      error("Failed to attach document", err.message || "Could not attach document");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Activity
  const handleDeleteActivity = async (actId: string) => {
    try {
      await api.delete(`/api/DealActivity/Delete/${actId}`);
      success("Deleted", "Activity item removed");
      setActivities((prev) => prev.filter((a) => a.id !== actId));
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete activity");
    }
  };

  // WhatsApp launcher: chat popup through the company WhatsApp number (falls back to wa.me without access)
  const handleLaunchWhatsApp = () => {
    const rawPhone = deal?.primary_contact?.mobile || deal?.primary_contact?.phone;
    if (!rawPhone) {
      error("No Phone Found", "Primary contact does not have a phone number saved.");
      return;
    }
    if (canSendWhatsApp) {
      setWaChatOpen(true);
      return;
    }
    const cleanPhone = rawPhone.replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  // Mail launcher: compose popup sent from the rep's connected mailbox
  const handleLaunchMail = () => {
    const email = deal?.primary_contact?.email || "";
    if (!canSendEmails) {
      if (!email) {
        error("No Email Found", "Primary contact does not have an email saved.");
        return;
      }
      window.open(`mailto:${email}?subject=Follow-up:%20${encodeURIComponent(deal?.name || "Deal")}`, "_self");
      return;
    }
    if (!mailbox || mailbox.sync_status === "AUTH_FAILED") {
      error(
        mailbox ? "Mailbox login failed" : "Mailbox not connected",
        "Connect your mailbox in the Emails tab to send emails directly from the CRM."
      );
      navigate("/emails?tab=settings");
      return;
    }
    setComposeDefaults({ to: email, subject: "" });
    setComposeOpen(true);
  };

  const handleReplyEmail = (t: EmailThread) => {
    setComposeDefaults(replyDefaults(t, mailbox?.email_address));
    setComposeOpen(true);
  };

  const handleEmailOpened = () => {
    if (!id) return;
    api.patch(`/api/DealEmail/MarkDealRead/${id}`).catch(() => undefined);
    setEmails((list) => list.map((e) => ({ ...e, is_read: true })));
  };

  const emailThreads = useMemo(() => groupEmailThreads(emails), [emails]);

  // The newest thing that happened on the deal (for "Last touch" in the header)
  const lastTouch = useMemo(() => {
    const WHAT: Record<string, string> = { NOTE: "Note", CALL: "Call", MEETING: "Meeting", EMAIL: "Email", WHATSAPP: "WhatsApp", FILE: "File" };
    const seen: { at: number; what: string }[] = [];
    for (const a of activities) seen.push({ at: new Date(a.created_on).getTime(), what: WHAT[a.activity_type] || "Activity" });
    for (const t of emailThreads) seen.push({ at: new Date(t.last.email_date).getTime(), what: "Email" });
    for (const w of waThreads) seen.push({ at: new Date(w.conversation.last_message_at || 0).getTime(), what: "WhatsApp" });
    const best = seen.filter((x) => Number.isFinite(x.at) && x.at > 0).sort((a, b) => b.at - a.at)[0];
    return best ? { at: new Date(best.at).toISOString(), what: best.what } : null;
  }, [activities, emailThreads, waThreads]);
  const mailboxReady = Boolean(mailbox && mailbox.sync_status !== "AUTH_FAILED");

  // Filtered timeline items: activities plus email conversations, newest first
  type TimelineEntry =
    | { kind: "activity"; at: number; act: DealActivityRecord }
    | { kind: "email"; at: number; thread: EmailThread }
    | { kind: "whatsapp"; at: number; wa: WhatsAppThread };

  const dayLabel = (at: number) => {
    const d = new Date(at);
    const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(at).setHours(0, 0, 0, 0)) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return d.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
  };

  const filteredTimeline = useMemo<TimelineEntry[]>(() => {
    if (activeTab === "CHANGELOG") {
      return [];
    }

    let items = [...activities];
    let includeEmails = activeTab === "ALL";
    let includeWhatsApp = activeTab === "ALL";

    if (activeTab === "NOTE") {
      items = items.filter((a) => a.activity_type === "NOTE");
    } else if (activeTab === "CALL") {
      items = items.filter((a) => a.activity_type === "CALL");
    } else if (activeTab === "MEETING") {
      items = items.filter((a) => a.activity_type === "MEETING");
    } else if (activeTab === "WHATSAPP") {
      items = items.filter((a) => a.activity_type === "WHATSAPP");
      includeWhatsApp = true;
    } else if (activeTab === "FILE") {
      items = items.filter((a) => a.activity_type === "FILE");
    } else if (activeTab === "EMAIL") {
      items = items.filter((a) => a.activity_type === "EMAIL");
      includeEmails = true;
    } else if (activeTab === "PLANNED") {
      items = items.filter(
        (a) => a.scheduled_at && new Date(a.scheduled_at).getTime() > Date.now()
      );
    }

    const entries: TimelineEntry[] = items.map((act) => ({
      kind: "activity",
      at: new Date(act.created_on).getTime(),
      act,
    }));
    if (includeEmails) {
      for (const thread of emailThreads) {
        entries.push({ kind: "email", at: new Date(thread.last.email_date).getTime(), thread });
      }
    }
    if (includeWhatsApp) {
      for (const wa of waThreads) {
        entries.push({ kind: "whatsapp", at: new Date(wa.conversation.last_message_at || 0).getTime(), wa });
      }
    }
    return entries.sort((a, b) => b.at - a.at);
  }, [activities, activeTab, emailThreads, waThreads]);

  // Filtered changelog items
  const filteredChangelogs = useMemo(() => {
    let list = [...changelogs];

    if (changelogFilter === "STAGE") {
      list = list.filter((l) => l.change_type === "STAGE_CHANGE" || l.change_type === "STATUS_CHANGE");
    } else if (changelogFilter === "DATA") {
      list = list.filter((l) => l.change_type === "DEAL_UPDATE" || l.change_type === "DEAL_CREATED");
    } else if (changelogFilter === "ACTIVITY") {
      list = list.filter(
        (l) =>
          l.change_type === "NOTE" ||
          l.change_type === "CALL" ||
          l.change_type === "MEETING" ||
          l.change_type === "WHATSAPP" ||
          l.change_type === "EMAIL" ||
          l.change_type === "FILE" ||
          l.change_type === "ACTIVITY_DELETED"
      );
    }

    if (changelogSearch.trim()) {
      const q = changelogSearch.toLowerCase();
      list = list.filter(
        (l) =>
          (l.title && l.title.toLowerCase().includes(q)) ||
          (l.description && l.description.toLowerCase().includes(q)) ||
          (l.field_name && l.field_name.toLowerCase().includes(q)) ||
          (l.old_value && l.old_value.toLowerCase().includes(q)) ||
          (l.new_value && l.new_value.toLowerCase().includes(q)) ||
          (l.created_by?.first_name && l.created_by.first_name.toLowerCase().includes(q)) ||
          (l.created_by?.last_name && l.created_by.last_name.toLowerCase().includes(q))
      );
    }

    return list;
  }, [changelogs, changelogFilter, changelogSearch]);

  const getChangelogIcon = (type: string, title: string) => {
    switch (type) {
      case "STAGE_CHANGE":
        return <ArrowRightLeft className="size-3.5 text-primary" />;
      case "STATUS_CHANGE":
        if (title.toLowerCase().includes("won")) {
          return <CheckCircle2 className="size-3.5 text-success" />;
        }
        if (title.toLowerCase().includes("lost")) {
          return <XCircle className="size-3.5 text-danger" />;
        }
        return <ArrowRightLeft className="size-3.5 text-primary" />;
      case "DEAL_UPDATE":
        return <Edit className="size-3.5 text-warning" />;
      case "DEAL_CREATED":
        return <Sparkles className="size-3.5 text-primary" />;
      case "NOTE":
        return <StickyNote className="size-3.5 text-warning" />;
      case "CALL":
        return <Phone className="size-3.5 text-primary" />;
      case "MEETING":
        return <Calendar className="size-3.5 text-accent-foreground" />;
      case "WHATSAPP":
        return <WhatsAppIcon className="size-3.5" />;
      case "EMAIL":
        return <Mail className="size-3.5 text-danger" />;
      case "FILE":
        return <Paperclip className="size-3.5 text-info" />;
      case "ACTIVITY_DELETED":
        return <Trash2 className="size-3.5 text-danger" />;
      default:
        return <History className="size-3.5 text-primary" />;
    }
  };

  const getChangelogIconBg = (type: string, title: string) => {
    switch (type) {
      case "STAGE_CHANGE":
        return "bg-primary/10 border-primary/20";
      case "STATUS_CHANGE":
        if (title.toLowerCase().includes("won")) {
          return "bg-success/10 border-success/20";
        }
        if (title.toLowerCase().includes("lost")) {
          return "bg-danger/10 border-danger/20";
        }
        return "bg-primary/10 border-primary/20";
      case "DEAL_UPDATE":
        return "bg-warning/10 border-warning/20";
      case "DEAL_CREATED":
        return "bg-primary/10 border-primary/20";
      case "NOTE":
        return "bg-warning/10 border-warning/20";
      case "CALL":
        return "bg-primary/10 border-primary/20";
      case "MEETING":
        return "bg-primary/10 border-primary/20";
      case "WHATSAPP":
        return "bg-success/10 border-success/20";
      case "EMAIL":
        return "bg-danger/10 border-danger/20";
      case "FILE":
        return "bg-info/10 border-info/20";
      case "ACTIVITY_DELETED":
        return "bg-danger/10 border-danger/20";
      default:
        return "bg-primary/10 border-primary/20";
    }
  };

  if (problem) return <RecordProblem error={problem} what="deal" record={{ type: "DEAL", id }} backTo="/deals" backLabel="Back to deals" onRetry={loadDealData} />;

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <FormSkeleton />
      </div>
    );
  }

  if (!deal) return null;

  const isWon = deal.deal_status === "WON";
  const isLost = deal.deal_status === "LOST";

  return (
    <DealGatesProvider dealId={deal.id} dealName={deal.name} accountId={deal.account_id} isClosed={isWon || isLost} onChanged={loadDealData}>
    <div className="space-y-3.5 pb-2">
      <DealRecordHeader
        deal={deal}
        summary={summary}
        busy={actionLoading}
        canWrite={hasPermission("deal.update")}
        lastTouch={lastTouch}
        onBack={() => navigate("/deals")}
        onEdit={openEditDeal}
        onSendQuote={sendQuote}
        onMarkLost={() => setLostOpen(true)}
        onMarkWon={openWonDialog}
        onCall={deal.deal_status === "OPEN" ? () => setCallOpen(true) : undefined}
        onWhatsApp={deal.deal_status === "OPEN" && canSendWhatsApp ? handleLaunchWhatsApp : undefined}
        onEmail={deal.deal_status === "OPEN" && canSendEmails ? handleLaunchMail : undefined}
        pinned={pins.isPinned(`/deals/${deal.id}`)}
        onTogglePin={() => pins.toggle(`/deals/${deal.id}`, deal.name).catch((e) => error("Could not change the pin", e?.message || "Try again"))}
        onStageClick={(stageId) => {
          const st = pipelineStages.find((x) => x.id === stageId);
          if (st) handleStageClick(st);
        }}
      />

      <div className="flex flex-wrap items-start gap-3.5">
        <div className="min-w-0 flex-[999_1_560px] space-y-3.5">
          <DealNextStep
            dealId={deal.id}
            isClosed={isWon || isLost}
            phone={deal.primary_contact?.mobile || deal.primary_contact?.phone}
            version={taskVersion}
            visitCustomer={deal.account_id ? { type: "ACCOUNT", id: deal.account_id, label: deal.account?.name || "Account", dealId: deal.id, dealLabel: deal.name } : null}
            onAdd={() => setTaskOpen(true)}
            onChanged={() => setTaskVersion((v) => v + 1)}
          />

          <section className="flex flex-col rounded-xl border border-border bg-card">
            <DealTabs
              value={section}
              onChange={setSection}
              counts={{
                activity: activities.length + emailThreads.length + waThreads.length,
                products: lineItemCount,
                files: activities.filter((a) => a.activity_type === "FILE").length,
                history: changelogs.length,
              }}
            />
            <div className="flex flex-col gap-3 p-4">
              {section === "activity" && (
                <DealComposer
                  text={noteText}
                  onText={setNoteText}
                  busy={actionLoading}
                  onSaveNote={() => handleAddNote({ preventDefault() {} } as React.FormEvent)}
                  onCall={() => setCallOpen(true)}
                  onMeeting={openMeetingModal}
                  onEmail={handleLaunchMail}
                  onWhatsApp={handleLaunchWhatsApp}
                  onFile={openFileModal}
                  onTask={deal.deal_status === "OPEN" ? () => setTaskOpen(true) : undefined}
                />
              )}

            {section === "activity" && (
              <div role="group" aria-label="Show" className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["ALL", "All", activities.length + emailThreads.length + waThreads.length],
                    ["NOTE", "Notes", activities.filter((a) => a.activity_type === "NOTE").length],
                    ["CALL", "Calls", activities.filter((a) => a.activity_type === "CALL").length],
                    ["EMAIL", "Emails", activities.filter((a) => a.activity_type === "EMAIL").length + emailThreads.length],
                    ["WHATSAPP", "WhatsApp", activities.filter((a) => a.activity_type === "WHATSAPP").length + waThreads.length],
                    ["MEETING", "Meetings", activities.filter((a) => a.activity_type === "MEETING").length],
                    ["PLANNED", "Planned", activities.filter((a) => a.scheduled_at && new Date(a.scheduled_at).getTime() > Date.now()).length],
                  ] as [ActivityTab, string, number][]
                ).map(([key, label, n]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={activityFilter === key}
                    onClick={() => setActivityFilter(key)}
                    className={`h-7 rounded-full border px-3 text-xs font-medium ${activityFilter === key ? "border-primary bg-accent text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
                  >
                    {label}
                    {n > 0 && <span className="ml-1 font-mono text-[11px] opacity-70">{n}</span>}
                  </button>
                ))}
              </div>
            )}

              {section === "files" && !(isWon || isLost) && (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={openFileModal}>
                    <Paperclip className="mr-1.5 size-3.5" />
                    Add a file
                  </Button>
                </div>
              )}
              {section === "qualification" && <DealQualificationTab />}
              {section === "products" && (
                <div className="flex flex-col gap-3">
                  <DealProductsCard
                    dealId={deal.id}
                    accountId={deal.account_id}
                    isClosed={isWon || isLost}
                    onChanged={(count, amountChanged) => {
                      setLineItemCount(count);
                      if (amountChanged) loadDealData();
                    }}
                  />
                  <DealQuotesCard dealId={deal.id} isClosed={isWon || isLost} />
                  <SamplesCard dealId={deal.id} canRequest={!(isWon || isLost)} />
                  <DealOrdersCard dealId={deal.id} isWon={isWon} />
                </div>
              )}
              {section === "proposals" && <DealProposalsCard dealId={deal.id} dealName={deal.name} stageKey={deal.stage?.stage_key} isClosed={isWon || isLost} />}
              {section === "agreements" && <DealAgreementsCard dealId={deal.id} dealName={deal.name} isClosed={isWon || isLost} />}

          {/* Content Area: Changelogs Tab */}
          {activeTab === "CHANGELOG" && (
            <div className="bg-card border rounded-lg p-4 sm:p-5 shadow-xs flex flex-col space-y-4">
              {/* Tab Header & Filters */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-3">
                <div className="flex items-center gap-2">
                  <History className="size-4.5 text-primary" />
                  <div>
                    <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                      Deal Audit & Activity Changelog
                      <span className="text-[11px] font-normal text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full border border-border/60">
                        {filteredChangelogs.length} {filteredChangelogs.length === 1 ? "entry" : "entries"}
                      </span>
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                      Complete audit trail of data changes, stage progressions, and communications inside this deal
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  {/* Search */}
                  <div className="relative w-full sm:w-44">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search changes..."
                      value={changelogSearch}
                      onChange={(e) => setChangelogSearch(e.target.value)}
                      className="w-full pl-7 pr-2.5 py-1 text-xs rounded-xl bg-muted/50 border border-border/70 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  {/* Filter Pills */}
                  <div className="flex items-center gap-1 bg-muted/50 p-0.5 rounded-xl border border-border/60 text-[11px]">
                    {(
                      [
                        { id: "ALL", label: "All" },
                        { id: "STAGE", label: "Stages" },
                        { id: "DATA", label: "Data" },
                        { id: "ACTIVITY", label: "Activities" },
                      ] as const
                    ).map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setChangelogFilter(f.id)}
                        className={`px-2 py-0.5 rounded-lg font-medium transition-all ${
                          changelogFilter === f.id
                            ? "bg-background text-foreground shadow-xs font-semibold"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Changelogs Timeline List */}
              <div className="overflow-y-auto max-h-[640px] xl:max-h-[750px] pr-2">
                {filteredChangelogs.length === 0 ? (
                  <div className="py-14 text-center space-y-2">
                    <div className="size-10 rounded-full bg-muted/60 flex items-center justify-center mx-auto text-muted-foreground">
                      <History className="size-5" />
                    </div>
                    <p className="text-xs font-medium text-foreground">No Changes Found</p>
                    <p className="text-[11px] text-muted-foreground max-w-xs mx-auto">
                      {changelogSearch || changelogFilter !== "ALL"
                        ? "No change logs match your current filter or search criteria."
                        : "All deal updates, stage moves, notes, calls, and meetings will be logged here with complete change history."}
                    </p>
                  </div>
                ) : (
                  <div className="relative border-l border-border/60 ml-3.5 space-y-3.5 pl-5 py-2">
                    {filteredChangelogs.map((log) => {
                      const authorName = log.created_by
                        ? `${log.created_by.first_name || ""} ${log.created_by.last_name || ""}`.trim() || log.created_by.email || "System User"
                        : "Super Admin";

                      const authorInitials = log.created_by
                        ? `${log.created_by.first_name?.[0] || ""}${log.created_by.last_name?.[0] || ""}`.toUpperCase() || "SA"
                        : "SA";

                      return (
                        <div key={log.id} className="relative group">
                          {/* Node icon on timeline */}
                          <div
                            className={`absolute -left-[31px] top-0 size-6 rounded-full flex items-center justify-center border ${getChangelogIconBg(
                              log.change_type,
                              log.title
                            )} bg-card shadow-xs`}
                          >
                            {getChangelogIcon(log.change_type, log.title)}
                          </div>

                          {/* Content Box */}
                          <div className="bg-background hover:border-border transition-colors border border-border/60 rounded-xl p-3 space-y-2 shadow-2xs">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-xs text-foreground">
                                  {log.title}
                                </span>
                                {log.field_name && (
                                  <span className="text-[10px] font-medium bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md border border-border/50">
                                    {log.field_name}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2 text-muted-foreground shrink-0">
                                {/* By Who Pill */}
                                <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground/80 bg-background/80 px-2 py-0.5 rounded-full border border-border/60 shadow-2xs">
                                  <div className="size-3.5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[9px] font-bold">
                                    {authorInitials}
                                  </div>
                                  <span className="truncate max-w-[120px]">{authorName}</span>
                                </div>

                                {/* Date & Time */}
                                <span className="text-[11px] text-muted-foreground font-mono">
                                  {new Date(log.created_on).toLocaleString()}
                                </span>
                              </div>
                            </div>

                            {/* Old to New Visual Diff */}
                            {log.old_value !== undefined && log.old_value !== null && log.new_value !== undefined && log.new_value !== null && (
                              <div className="flex items-center gap-2 text-xs flex-wrap pt-0.5">
                                <div className="flex items-center gap-1.5 bg-destructive/10 text-destructive border border-destructive/20 px-2.5 py-0.5 rounded-md font-mono text-[11px]">
                                  <span className="text-[9px] uppercase font-bold text-destructive/70 tracking-wider">
                                    Old:
                                  </span>
                                  <span className="line-through">{log.old_value}</span>
                                </div>
                                <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                                <div className="flex items-center gap-1.5 bg-success/10 text-success border border-success/20 px-2.5 py-0.5 rounded-md font-mono text-[11px] font-medium">
                                  <span className="text-[9px] uppercase font-bold text-success/70 tracking-wider">
                                    New:
                                  </span>
                                  <span>{log.new_value}</span>
                                </div>
                              </div>
                            )}

                            {/* Single Value Display if no old value */}
                            {!log.old_value && log.new_value && log.change_type !== "NOTE" && (
                              <div className="flex items-center gap-1.5 bg-background/80 text-foreground border border-border/60 px-2.5 py-1 rounded-md text-xs">
                                <span className="text-[9px] uppercase font-bold text-muted-foreground tracking-wider">
                                  Value:
                                </span>
                                <span className="font-medium text-xs break-all">{log.new_value}</span>
                              </div>
                            )}

                            {/* Description / Note Content / Lost Reason */}
                            {log.description && (
                              <p className="text-xs text-muted-foreground bg-background/60 p-2.5 rounded-xl border border-border/40 whitespace-pre-wrap">
                                {log.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <InfiniteScroll hasMore={feedHasMore(changelogMeta)} loading={changelogMeta.loading} onLoad={loadMoreChangelog} />
              </div>
            </div>
          )}


          {/* Content Area: Activities Feed */}
          {(section === "activity" || section === "files") && (
            <div className="flex flex-col">
              <div>
                {filteredTimeline.length === 0 ? (
                  <div className="py-10 text-center space-y-2">
                    <div className="size-9 rounded-full bg-muted/60 border flex items-center justify-center mx-auto text-muted-foreground">
                      <Clock className="size-4" />
                    </div>
                    <p className="text-xs font-medium text-foreground">No Activities Found</p>
                    <p className="text-[11px] text-muted-foreground max-w-sm mx-auto">
                      Use the quick action buttons above to record notes, log phone calls, schedule client meetings, or attach documents.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredTimeline.map((entry, idx) => {
                      const label = dayLabel(entry.at);
                      const heading =
                        idx === 0 || dayLabel(filteredTimeline[idx - 1]!.at) !== label ? (
                          <h3 className="mb-1.5 mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">{label}</h3>
                        ) : null;
                      const node = (() => {
                      if (entry.kind === "whatsapp") {
                        return (
                          <WhatsAppTimelineItem
                            key={`wa-${entry.wa.conversation.id}`}
                            thread={entry.wa}
                            onOpenChat={() => navigate(`/whatsapp?c=${entry.wa.conversation.id}`)}
                          />
                        );
                      }
                      if (entry.kind === "email") {
                        return (
                          <EmailThreadItem
                            key={`email-${entry.thread.id}`}
                            thread={entry.thread}
                            canReply={canSendEmails && mailboxReady}
                            onReply={handleReplyEmail}
                            onOpened={handleEmailOpened}
                          />
                        );
                      }
                      const act = entry.act;
                      const isNote = act.activity_type === "NOTE";
                      const isCall = act.activity_type === "CALL";
                      const isMeeting = act.activity_type === "MEETING";
                      const isFile = act.activity_type === "FILE";

                      return (
                        <div key={act.id} className="relative group">
                          <div className="bg-card border border-border rounded-lg px-3.5 py-2.5 space-y-1.5">
                            <div className="flex items-center justify-between text-xs gap-2">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] font-semibold tracking-wide ${
                                    isNote
                                      ? "bg-warning/10 text-warning border-warning/30"
                                      : isCall
                                      ? "bg-primary/10 text-primary border-primary/30"
                                      : isMeeting
                                      ? "bg-primary/10 text-accent-foreground border-primary/30"
                                      : isFile
                                      ? "bg-primary/10 text-accent-foreground border-primary/30"
                                      : "bg-success/10 text-success border-success/30"
                                  }`}
                                >
                                  {act.activity_type}
                                </Badge>
                                <span className="font-semibold text-foreground text-xs">
                                  {act.title}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground font-mono">
                                  {new Date(act.created_on).toLocaleString(dateLocale(), withClock({
                                    dateStyle: "medium",
                                    timeStyle: "short" }))}
                                </span>
                                <button
                                  onClick={() => handleDeleteActivity(act.id)}
                                  title="Delete Activity"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Extra Metadata for Calls / Meetings */}
                            {isCall && (
                              <div className="space-y-1.5 pt-0.5">
                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                  {act.call_direction && (
                                    <Badge variant="secondary" className="text-[10px] font-medium gap-1 px-1.5 py-0.5">
                                      {act.call_direction === "INBOUND" ? (
                                        <PhoneIncoming className="size-2.5 text-primary" />
                                      ) : (
                                        <PhoneOutgoing className="size-2.5 text-success" />
                                      )}
                                      {act.call_direction === "INBOUND" ? "Inbound" : "Outbound"}
                                    </Badge>
                                  )}
                                  {act.call_outcome && (
                                    <span className="text-[11px] font-medium text-foreground bg-muted/60 px-2 py-0.5 rounded-md border border-border/50">
                                      {act.call_outcome}
                                    </span>
                                  )}
                                  {act.call_duration !== undefined && act.call_duration > 0 && (
                                    <span className="text-[11px] text-muted-foreground font-mono">
                                      ⏱️ {act.call_duration} min
                                    </span>
                                  )}
                                  {act.sentiment && (
                                    <span
                                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                                        act.sentiment === "HOT"
                                          ? "bg-danger/10 text-danger border-danger/30"
                                          : act.sentiment === "WARM"
                                          ? "bg-warning/10 text-warning border-warning/30"
                                          : "bg-primary/10 text-primary border-primary/30"
                                      }`}
                                    >
                                      {act.sentiment === "HOT" ? "🔥 Hot Interest" : act.sentiment === "WARM" ? "☀️ Warm" : "❄️ Cold"}
                                    </span>
                                  )}
                                </div>
                                {act.next_followup_date && (
                                  <div className="text-[11px] text-primary bg-primary/5 border border-primary/20 px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                                    <Clock className="size-3 shrink-0" />
                                    <span>Next Follow-up: <strong>{new Date(act.next_followup_date).toLocaleString(dateLocale(), withClock({ dateStyle: "medium", timeStyle: "short" }))}</strong></span>
                                  </div>
                                )}
                              </div>
                            )}

                            {isMeeting && (
                              <div className="space-y-2 pt-0.5">
                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                  {act.meeting_mode && (
                                    <Badge variant="outline" className="text-[10px] font-medium gap-1 px-2 py-0.5 bg-primary/10 text-accent-foreground border-primary/20">
                                      {act.meeting_mode.includes("MEET") || act.meeting_mode.includes("ZOOM") ? (
                                        <Video className="size-2.5" />
                                      ) : (
                                        <MapPin className="size-2.5" />
                                      )}
                                      {act.meeting_mode.replace(/_/g, " ")}
                                    </Badge>
                                  )}
                                  {act.meeting_status && (
                                    <Badge
                                      variant="secondary"
                                      className={`text-[10px] font-semibold px-2 py-0.5 ${
                                        act.meeting_status === "COMPLETED"
                                          ? "bg-success/10 text-success border-success/20"
                                          : act.meeting_status === "CANCELLED"
                                          ? "bg-destructive/10 text-destructive border-destructive/20"
                                          : "bg-primary/10 text-primary border-primary/20"
                                      }`}
                                    >
                                      {act.meeting_status}
                                    </Badge>
                                  )}
                                  {act.scheduled_at && (
                                    <span className="text-[11px] text-foreground font-medium">
                                      📅 {new Date(act.scheduled_at).toLocaleString(dateLocale(), withClock({ dateStyle: "medium", timeStyle: "short" }))}
                                    </span>
                                  )}
                                  {!!act.meeting_duration && (
                                    <span className="text-[11px] text-muted-foreground font-mono">
                                      ({act.meeting_duration}m)
                                    </span>
                                  )}
                                </div>

                                {act.meeting_link && (
                                  <div className="flex items-center gap-2 pt-0.5">
                                    <a
                                      href={act.meeting_link.startsWith("http") ? act.meeting_link : `https://${act.meeting_link}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 border border-primary/20 px-3 py-1 rounded-lg transition-colors"
                                    >
                                      <Video className="size-3.5" />
                                      Join Video Meeting
                                      <ExternalLink className="size-3" />
                                    </a>
                                  </div>
                                )}

                                {act.attendees && (
                                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 bg-muted/40 px-2 py-1 rounded-md border border-border/40">
                                    <Users className="size-3 text-muted-foreground shrink-0" />
                                    <span className="font-semibold text-foreground">Attendees:</span>
                                    <span className="truncate">{act.attendees}</span>
                                  </div>
                                )}

                                {act.next_action && (
                                  <div className="text-[11px] text-warning bg-warning/10 px-2 py-1 rounded-md border border-warning/20 flex items-center gap-1.5">
                                    <ArrowRight className="size-3 shrink-0" />
                                    <span className="font-semibold">Action Item:</span>
                                    <span>{act.next_action}</span>
                                  </div>
                                )}
                                <VisitTimelineExtra visit={(act as any).visit} label={act.title} />
                              </div>
                            )}

                            {isFile && (
                              <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/40 border border-border/60 text-xs">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="size-8 rounded-lg bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
                                    <FileText className="size-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-foreground truncate">
                                      {act.file_name || "Attachment"}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground truncate">
                                      {isUploadedFile(act.file_url) ? "Uploaded File" : "File link"}
                                    </p>
                                  </div>
                                </div>
                                {fileHref(act) && (
                                  <a
                                    href={fileHref(act)!}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline shrink-0 bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded-md transition-colors"
                                  >
                                    <ExternalLink className="size-3" />
                                    {isUploadedFile(act.file_url) ? "View / Download" : "Open Link"}
                                  </a>
                                )}
                              </div>
                            )}

                            {act.description_html ? (
                              <div
                                className="rte-content text-xs text-foreground/90 leading-relaxed pt-0.5"
                                // Sanitized on the server to formatting tags only
                                dangerouslySetInnerHTML={{ __html: act.description_html }}
                              />
                            ) : (
                              act.description && (
                                <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed pt-0.5">
                                  {act.description}
                                </p>
                              )
                            )}

                            <RecordingPlayer callId={(act as any).call_id} />
                            {(act as any).call_id && <CallAiActions callId={(act as any).call_id} variant="link" />}

                            {act.created_by && (
                              <div className="text-[11px] text-muted-foreground pt-0.5 flex items-center gap-1.5">
                                <span>Logged by:</span>
                                <span className="font-medium text-foreground">
                                  {act.created_by.first_name} {act.created_by.last_name || ""}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                      })();
                      return (
                        <React.Fragment key={`${entry.kind}-${entry.at}-${idx}`}>
                          {heading}
                          {node}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
                <InfiniteScroll hasMore={timelineHasMore} loading={timelineLoading} onLoad={loadMoreTimeline} />
              </div>
            </div>
          )}

            </div>
          </section>
        </div>

        <aside className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5 lg:max-w-[380px]">
          <DealMoveCard />
          <DealHealthCard dealId={deal.id} isClosed={isWon || isLost} />
          <DealRelated deal={deal} summary={summary} canWrite={hasPermission("deal.update")} productLines={lineItemCount} onOpenProducts={() => setSection("products")} />
          <DealTasksCard key={`tasks-${taskVersion}`} dealId={deal.id} dealName={deal.name} isClosed={isWon || isLost} />
        </aside>
      </div>

      {/* --- DIALOGS --- */}

      {/* Task from the composer */}
      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        defaultRelated={{ type: "DEAL", id: deal.id, name: deal.name }}
        onSaved={() => setTaskVersion((v) => v + 1)}
      />

      {/* WhatsApp chat (WhatsApp quick action) */}
      {(deal.primary_contact?.mobile || deal.primary_contact?.phone) && (
        <WhatsAppChatDialog
          open={waChatOpen}
          onOpenChange={(v) => {
            setWaChatOpen(v);
            if (!v) loadDealData();
          }}
          phone={(deal.primary_contact.mobile || deal.primary_contact.phone)!}
          name={`${deal.primary_contact.first_name} ${deal.primary_contact.last_name || ""}`.trim()}
          dealId={deal.id}
          contactId={deal.primary_contact.id}
        />
      )}

      {/* Email compose (Mail quick action and Reply) */}
      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        dealId={deal.id}
        dealName={deal.name}
        contactName={
          deal.primary_contact
            ? `${deal.primary_contact.first_name} ${deal.primary_contact.last_name || ""}`.trim()
            : undefined
        }
        defaults={composeDefaults}
        onSent={() => {
          setSection("activity");
          setActivityFilter("EMAIL");
          loadDealData();
        }}
      />

      {/* 1. Add Note Dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="size-5 text-warning shrink-0" />
              <span>Add Deal Note</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Record important details, observations, or next steps for <span className="font-medium text-foreground">{deal.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddNote} className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Note Content</Label>
              <RichTextEditor
                value={noteHtml}
                onChange={(html, text) => {
                  setNoteHtml(html);
                  setNoteText(text);
                }}
                placeholder="Enter note here..."
                minHeight={160}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNoteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading || !noteText.trim()}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Save Note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 2. Log Call Dialog */}
      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="size-5 text-primary shrink-0" />
              <span>Log Phone Call</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Record call outcome, client sentiment, and schedule follow-up reminders.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogCall} className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Call Subject / Title *</Label>
              <Input
                required
                placeholder="e.g. Follow-up on pricing discussion"
                value={callForm.title}
                onChange={(e) => setCallForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Call Direction</Label>
                <Select
                  value={callForm.direction}
                  onValueChange={(v) => setCallForm((p) => ({ ...p, direction: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OUTBOUND">Outbound (We Called)</SelectItem>
                    <SelectItem value="INBOUND">Inbound (Client Called)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Lead Sentiment / Interest</Label>
                <Select
                  value={callForm.sentiment}
                  onValueChange={(v) => setCallForm((p) => ({ ...p, sentiment: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HOT">🔥 Hot / High Interest</SelectItem>
                    <SelectItem value="WARM">☀️ Warm / Interested</SelectItem>
                    <SelectItem value="COLD">❄️ Cold / Low Interest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Call Outcome</Label>
                <Select
                  value={callForm.outcome}
                  onValueChange={(v) => setCallForm((p) => ({ ...p, outcome: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Connected">Connected / Spoke</SelectItem>
                    <SelectItem value="Left Voicemail">Left Voicemail</SelectItem>
                    <SelectItem value="Busy / No Answer">Busy / No Answer</SelectItem>
                    <SelectItem value="Wrong Number">Wrong Number</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Duration (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g. 5"
                  value={callForm.duration}
                  onChange={(e) => setCallForm((p) => ({ ...p, duration: Number(e.target.value) }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium flex items-center justify-between">
                <span>Next Follow-up Reminder (Optional)</span>
                <span className="text-[11px] text-muted-foreground font-normal">Auto-creates task</span>
              </Label>
              <Input
                type="datetime-local"
                value={callForm.next_followup_date}
                onChange={(e) => setCallForm((p) => ({ ...p, next_followup_date: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Call Summary / Notes</Label>
              <Textarea
                rows={3}
                placeholder="What was discussed with the client?"
                value={callForm.notes}
                onChange={(e) => setCallForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCallOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Log Call
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 3. Schedule Meeting Dialog */}
      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="size-5 text-accent-foreground shrink-0" />
              <span>Schedule / Log Meeting</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Book an upcoming client demo, presentation, or record meeting minutes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleMeeting} className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Meeting Title *</Label>
              <Input
                required
                placeholder="e.g. Solution Demo with Procurement Team"
                value={meetingForm.title}
                onChange={(e) => setMeetingForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Meeting Mode / Location</Label>
                <Select
                  value={meetingForm.meeting_mode}
                  onValueChange={(v) => setMeetingForm((p) => ({ ...p, meeting_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GOOGLE_MEET">Google Meet</SelectItem>
                    <SelectItem value="ZOOM">Zoom Video</SelectItem>
                    <SelectItem value="IN_PERSON">In-Person (Our Office)</SelectItem>
                    <SelectItem value="CLIENT_SITE">Client Site / Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Duration</Label>
                <Select
                  value={String(meetingForm.duration)}
                  onValueChange={(v) => setMeetingForm((p) => ({ ...p, duration: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Date & Time *</Label>
                <Input
                  type="datetime-local"
                  required
                  value={meetingForm.scheduled_at}
                  onChange={(e) => setMeetingForm((p) => ({ ...p, scheduled_at: e.target.value }))}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Meeting Status</Label>
                <Select
                  value={meetingForm.meeting_status}
                  onValueChange={(v) => setMeetingForm((p) => ({ ...p, meeting_status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="RESCHEDULED">Rescheduled</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled / No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Meeting link (optional)</Label>
              <Input
                type="url"
                placeholder="Paste the video meeting link"
                value={meetingForm.meeting_link}
                onChange={(e) => setMeetingForm((p) => ({ ...p, meeting_link: e.target.value }))}
              />
            </div>

            {/* Attendees Multi-select with Search & Select All */}
            <div className="space-y-1.5" ref={attendeesDropdownRef}>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs sm:text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Users className="size-3.5 text-primary shrink-0" />
                  <span>Attendees / Participants</span>
                </Label>
                <div className="flex items-center gap-2">
                  {selectedAttendees.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleUpdateAttendees([])}
                      className="text-[11px] text-muted-foreground hover:text-destructive transition-colors"
                    >
                      Clear all ({selectedAttendees.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setAttendeesDropdownOpen(!attendeesDropdownOpen)}
                    className="text-[11px] text-primary hover:underline font-medium flex items-center gap-0.5"
                  >
                    <span>{attendeesDropdownOpen ? "Hide list" : "+ Browse list"}</span>
                    <ChevronDown className={`size-3 transition-transform ${attendeesDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Box displaying selected attendee chips + trigger */}
              <div
                onClick={() => setAttendeesDropdownOpen(true)}
                className="min-h-10 w-full rounded-xl border border-border/80 bg-background hover:border-primary/60 p-2 flex flex-wrap items-center gap-1.5 cursor-pointer shadow-2xs transition-all focus-within:ring-1 focus-within:ring-primary"
              >
                {selectedAttendees.length === 0 ? (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5 px-1 py-0.5">
                    <Users className="size-3.5 text-muted-foreground/70 shrink-0" />
                    <span>Click to search & select contacts or team members...</span>
                  </span>
                ) : (
                  selectedAttendees.map((att) => (
                    <span
                      key={att}
                      className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/25 text-[11px] font-medium px-2 py-0.5 rounded-lg shadow-2xs group min-w-0"
                    >
                      <span className="max-w-[140px] sm:max-w-[220px] truncate">{att}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAttendee(att);
                        }}
                        className="hover:bg-primary/20 text-primary/70 hover:text-primary rounded p-0.5 transition-colors shrink-0"
                      >
                        <X className="size-2.5" />
                      </button>
                    </span>
                  ))
                )}
              </div>

              {/* Expandable Selector Panel (Inline so it never clips or overflows the modal) */}
              {attendeesDropdownOpen && (
                <div className="mt-2 bg-muted/30 text-popover-foreground border border-border rounded-xl shadow-xs p-2.5 space-y-2 flex flex-col animate-in fade-in-50 duration-150">
                  {/* Search Bar + Select All Button */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground shrink-0" />
                      <input
                        type="text"
                        autoFocus
                        placeholder="Search by name, role or email..."
                        value={attendeesSearch}
                        onChange={(e) => setAttendeesSearch(e.target.value)}
                        className="w-full pl-8 pr-2.5 py-1.5 text-xs sm:text-sm rounded-lg bg-background border border-border/80 focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSelectAllFiltered}
                      className="text-[11px] sm:text-xs font-semibold text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg border border-primary/25 shrink-0 transition-colors text-center"
                    >
                      Select All ({filteredAttendeeOptions.length})
                    </button>
                  </div>

                  {/* Scrollable list of attendees */}
                  <div className="overflow-y-auto max-h-48 space-y-1 pr-1 text-xs sm:text-sm">
                    {filteredAttendeeOptions.length === 0 && !attendeesSearch ? (
                      <div className="py-6 text-center text-xs text-muted-foreground">
                        No contacts or team members found
                      </div>
                    ) : null}

                    {filteredAttendeeOptions.map((opt) => {
                      const isSelected = selectedAttendees.includes(opt.name);
                      return (
                        <div
                          key={opt.id}
                          onClick={() => handleToggleAttendee(opt.name)}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors gap-2 ${
                            isSelected
                              ? "bg-primary/10 text-primary font-medium"
                              : "hover:bg-muted/70 text-foreground"
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div
                              className={`size-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                                opt.type === "contact"
                                  ? "bg-success/10 text-success border border-success/20"
                                  : "bg-primary/10 text-primary border border-primary/20"
                              }`}
                            >
                              {opt.name[0]?.toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-medium truncate">{opt.name}</p>
                              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                                {opt.role || opt.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded font-medium border ${
                                opt.type === "contact"
                                  ? "bg-success/10 text-success border-success/20"
                                  : "bg-primary/10 text-primary border-primary/20"
                              }`}
                            >
                              {opt.type === "contact" ? "Contact" : "Team"}
                            </span>
                            <div
                              className={`size-4 rounded border flex items-center justify-center transition-colors ${
                                isSelected
                                  ? "bg-primary border-primary text-primary-foreground"
                                  : "border-border/80"
                              }`}
                            >
                              {isSelected && <Check className="size-3" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Custom attendee option if search text is not in options */}
                    {attendeesSearch.trim() &&
                      !filteredAttendeeOptions.some(
                        (o) => o.name.toLowerCase() === attendeesSearch.trim().toLowerCase()
                      ) && (
                        <button
                          type="button"
                          onClick={() => {
                            handleToggleAttendee(attendeesSearch.trim());
                            setAttendeesSearch("");
                          }}
                          className="w-full text-left p-2 rounded-lg bg-muted/60 hover:bg-muted text-primary text-xs sm:text-sm flex items-center gap-1.5 font-medium border border-dashed border-border min-w-0"
                        >
                          <Plus className="size-3.5 shrink-0" />
                          <span className="truncate">Add "{attendeesSearch.trim()}" as custom attendee</span>
                        </button>
                      )}
                  </div>

                  {/* Footer Bar */}
                  <div className="flex items-center justify-between pt-1.5 border-t border-border/50 text-[11px] sm:text-xs text-muted-foreground">
                    <span>
                      {selectedAttendees.length} attendee{selectedAttendees.length === 1 ? "" : "s"} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setAttendeesDropdownOpen(false)}
                      className="px-3 py-1 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 text-xs shadow-2xs"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Action Item / Next Step (Optional)</Label>
              <Input
                placeholder="e.g. Share quotation by tomorrow"
                value={meetingForm.next_action}
                onChange={(e) => setMeetingForm((p) => ({ ...p, next_action: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Agenda / Meeting Notes</Label>
              <Textarea
                rows={3}
                placeholder="Meeting agenda, discussion points, or recap..."
                value={meetingForm.notes}
                onChange={(e) => setMeetingForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMeetingOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Save Meeting
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 4. Attach File Dialog */}
      <Dialog open={fileOpen} onOpenChange={setFileOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="size-5 text-accent-foreground shrink-0" />
              <span>Attach Document / File</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Upload a document from your computer or link from cloud storage.
            </DialogDescription>
          </DialogHeader>

          {/* Segmented Mode Picker */}
          <div className="grid grid-cols-2 gap-1.5 bg-muted p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setFileMode("upload")}
              className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all ${
                fileMode === "upload"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <UploadCloud className="size-4 shrink-0" />
              <span className="truncate">Upload Local File</span>
            </button>
            <button
              type="button"
              onClick={() => setFileMode("url")}
              className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all ${
                fileMode === "url"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Link2 className="size-4 shrink-0" />
              <span className="truncate">Link to a file</span>
            </button>
          </div>

          <form onSubmit={handleAttachFile} className="space-y-3.5 py-1">
            {fileMode === "upload" ? (
              <div className="space-y-3">
                {/* File Dropzone */}
                {!selectedFile ? (
                  <label className="border-2 border-dashed border-border/80 hover:border-primary/60 rounded-xl p-5 flex flex-col items-center justify-center gap-2 cursor-pointer bg-muted/20 hover:bg-muted/40 transition-colors">
                    <input
                      type="file"
                      accept={ALLOWED_FILE_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          setSelectedFile(f);
                          if (!fileForm.file_name.trim()) {
                            setFileForm((p) => ({ ...p, file_name: f.name }));
                          }
                        }
                      }}
                    />
                    <div className="size-10 rounded-full bg-primary/10 text-accent-foreground flex items-center justify-center">
                      <UploadCloud className="size-5" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        Click or drag & drop a file to upload
                      </p>
                      <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                        PDF, DOCX, XLSX, images, contracts up to 25MB
                      </p>
                    </div>
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-primary/5 border border-primary/20 text-xs sm:text-sm gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <FileText className="size-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-foreground truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-[11px] sm:text-xs text-muted-foreground">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                      }}
                      className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-muted transition-colors shrink-0"
                      title="Remove file"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Document Name / Title *</Label>
                  <Input
                    required
                    placeholder="e.g. Sales_Contract_Final.pdf"
                    value={fileForm.file_name}
                    onChange={(e) => setFileForm((p) => ({ ...p, file_name: e.target.value }))}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Document Name / Title *</Label>
                  <Input
                    required
                    placeholder="e.g. Project proposal"
                    value={fileForm.file_name}
                    onChange={(e) => setFileForm((p) => ({ ...p, file_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">File link *</Label>
                  <Input
                    required
                    type="url"
                    placeholder="Paste the file's share link"
                    value={fileForm.file_url}
                    onChange={(e) => setFileForm((p) => ({ ...p, file_url: e.target.value }))}
                  />
                  <p className="text-[11px] sm:text-xs text-muted-foreground">
                    The share link of a file you keep online. Make sure the people who need it can open it.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Remarks / Notes</Label>
              <Textarea
                rows={2}
                placeholder="Optional notes or description about this document..."
                value={fileForm.notes}
                onChange={(e) => setFileForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setFileOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  actionLoading ||
                  (fileMode === "upload" && !selectedFile) ||
                  (fileMode === "url" && (!fileForm.file_url.trim() || !fileForm.file_name.trim()))
                }
              >
                {actionLoading ? (
                  <Loader2 className="size-4 animate-spin mr-1.5" />
                ) : null}
                {fileMode === "upload" ? "Upload & Attach" : "Attach Link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StageMoveDialog request={stageMoveRequest} onClose={() => setStageMoveRequest(null)} onMoved={() => loadDealData()} />

      {/* 5. Mark Won Dialog */}
      <Dialog open={wonOpen} onOpenChange={setWonOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-success">
              <CheckCircle2 className="size-5 shrink-0" />
              <span>Mark Deal as Won</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Are you sure you want to mark <strong className="text-foreground">{deal.name}</strong> as Won? This records the revenue and advances the pipeline.
            </DialogDescription>
          </DialogHeader>
          {wonGate && !wonGate.allowed && (
            <div className="rounded-md border border-warning/30 bg-warning/10 p-3 space-y-2 text-xs text-warning">
              <p className="font-semibold">Not ready to close as Won:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {wonGate.missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              {wonGate.can_override ? (
                <div className="space-y-1">
                  <Label className="text-xs">Why close it as Won anyway? (saved in the history and security log)</Label>
                  <Input value={wonReason} onChange={(e) => setWonReason(e.target.value)} placeholder="e.g. Repeat order, paid upfront" />
                </div>
              ) : (
                <p className="text-muted-foreground">Upload the signed agreement or PO in the stage card first, or ask your Sales Head.</p>
              )}
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setWonOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleMarkWon}
              disabled={actionLoading || (!!wonGate && !wonGate.allowed && (!wonGate.can_override || !wonReason.trim()))}
              className="bg-success hover:bg-success/90 text-white"
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Confirm Won
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 6. Mark Lost Dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="sm:max-w-md md:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="size-5 shrink-0" />
              <span>Mark Deal as Lost</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Please record the reason why <strong className="text-foreground">{deal.name}</strong> did not close.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMarkLost} className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Lost Reason *</Label>
              <Select
                value={lostForm.lost_reason}
                onValueChange={(v) => setLostForm((p) => ({ ...p, lost_reason: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="-- Select lost reason --" />
                </SelectTrigger>
                <SelectContent>
                  {lostReasons.map((r) => (
                    <SelectItem key={r.code} value={r.name}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Detailed Lost Notes *</Label>
              <Textarea
                rows={3}
                required
                placeholder="Explain the circumstances..."
                value={lostForm.lost_notes}
                onChange={(e) => setLostForm((p) => ({ ...p, lost_notes: e.target.value }))}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLostOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={actionLoading || !lostForm.lost_notes.trim()}
              >
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Confirm Lost
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 7. Edit Deal Metadata Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="size-5 text-primary shrink-0" />
              <span>Edit Deal Details</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Update core deal value, close timeline, company, and primary contact.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-medium">Deal Name *</Label>
              <Input
                required
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Deal Amount (₹) *</Label>
                <Input
                  type="number"
                  min={0}
                  required
                  readOnly={lineItemCount > 0}
                  value={editForm.amount}
                  onChange={(e) => setEditForm((p) => ({ ...p, amount: Number(e.target.value) }))}
                  className="font-mono read-only:bg-muted read-only:cursor-not-allowed"
                />
                {lineItemCount > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Calculated from the deal's products.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Expected Close Date</Label>
                <Input
                  type="date"
                  value={editForm.expected_close_date}
                  onChange={(e) => setEditForm((p) => ({ ...p, expected_close_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Company Account</Label>
                <SearchSelect
                  endpoint="/api/Account/List"
                  value={editForm.account_id}
                  selectedLabel={editForm.account_id === deal?.account_id ? deal?.account?.name : undefined}
                  getLabel={(row) => row.name}
                  placeholder="Search accounts"
                  allowEmpty
                  emptyLabel="(No Account Linked)"
                  onChange={(id) => setEditForm((p) => ({ ...p, account_id: id }))}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Primary Contact</Label>
                <SearchSelect
                  endpoint="/api/Contact/List"
                  value={editForm.primary_contact_id}
                  selectedLabel={
                    editForm.primary_contact_id === deal?.primary_contact_id && deal?.primary_contact
                      ? `${deal.primary_contact.first_name || ""} ${deal.primary_contact.last_name || ""}`.trim()
                      : undefined
                  }
                  getLabel={(row) =>
                    `${row.first_name || ""} ${row.last_name || ""}`.trim() + (row.job_title ? ` (${row.job_title})` : "")
                  }
                  placeholder="Search contacts"
                  allowEmpty
                  emptyLabel="(No Contact Linked)"
                  onChange={(id) => setEditForm((p) => ({ ...p, primary_contact_id: id }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading || !editForm.name.trim()}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 8. Edit Company Account Dialog */}
      <Dialog open={editAccountOpen} onOpenChange={setEditAccountOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="size-5 text-primary shrink-0" />
              <span>Company Account</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Update company details or associate a different account with this deal.
            </DialogDescription>
          </DialogHeader>

          {/* Segmented Mode Picker */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-1 bg-muted p-1 rounded-xl">
            {deal.account_id && (
              <button
                type="button"
                onClick={() => setAccountEditMode("edit_current")}
                className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                  accountEditMode === "edit_current"
                    ? "bg-background text-foreground shadow-2xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Edit Current
              </button>
            )}
            <button
              type="button"
              onClick={() => setAccountEditMode("switch_account")}
              className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                accountEditMode === "switch_account"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Choose Existing
            </button>
            <button
              type="button"
              onClick={() => {
                setAccountEditMode("create_new");
                setAccountForm({
                  name: "",
                  industry: "",
                  website: "",
                  phone: "",
                  email: "",
                  billing_address: "",
                });
              }}
              className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                accountEditMode === "create_new"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Create New
            </button>
          </div>

          <form onSubmit={handleAccountSubmit} className="space-y-3.5 py-1">
            {accountEditMode === "switch_account" ? (
              <div className="space-y-1.5 py-1">
                <Label className="text-xs sm:text-sm font-medium">Select Customer Account *</Label>
                <SearchSelect
                  endpoint="/api/Account/List"
                  value={selectedAccountId}
                  selectedLabel={selectedAccountId === deal?.account_id ? deal?.account?.name : undefined}
                  getLabel={(row) => `${row.name}${row.phone ? ` (${row.phone})` : ""}`}
                  placeholder="Search accounts"
                  onChange={(id) => setSelectedAccountId(id)}
                />
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  Links this deal to an account already registered in the CRM.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Company Name *</Label>
                  <Input
                    required
                    placeholder="e.g. Acme Corporation"
                    value={accountForm.name}
                    onChange={(e) => setAccountForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Phone Number</Label>
                    <Input
                      placeholder="+91 98765 43210"
                      value={accountForm.phone}
                      onChange={(e) => setAccountForm((p) => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Email Address</Label>
                    <Input
                      type="email"
                      placeholder="info@company.com"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm((p) => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Industry</Label>
                    <Input
                      placeholder="e.g. Technology, Manufacturing"
                      value={accountForm.industry}
                      onChange={(e) => setAccountForm((p) => ({ ...p, industry: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Website</Label>
                    <Input
                      placeholder="www.company.com"
                      value={accountForm.website}
                      onChange={(e) => setAccountForm((p) => ({ ...p, website: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Billing Address</Label>
                  <Textarea
                    rows={2}
                    placeholder="Street, City, State, PIN"
                    value={accountForm.billing_address}
                    onChange={(e) => setAccountForm((p) => ({ ...p, billing_address: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditAccountOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  actionLoading ||
                  (accountEditMode === "switch_account" && !selectedAccountId) ||
                  (accountEditMode !== "switch_account" && !accountForm.name.trim())
                }
              >
                {actionLoading ? (
                  <Loader2 className="size-4 animate-spin mr-1.5" />
                ) : null}
                {accountEditMode === "edit_current"
                  ? "Save Details"
                  : accountEditMode === "switch_account"
                  ? "Link Account"
                  : "Create & Link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 9. Edit Primary Contact Dialog */}
      <Dialog open={editContactOpen} onOpenChange={setEditContactOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="size-5 text-primary shrink-0" />
              <span>Primary Contact</span>
            </DialogTitle>
            <DialogDescription className="break-words">
              Update contact person details or link a contact to this deal.
            </DialogDescription>
          </DialogHeader>

          {/* Segmented Mode Picker */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-1 bg-muted p-1 rounded-xl">
            {deal.primary_contact_id && (
              <button
                type="button"
                onClick={() => setContactEditMode("edit_current")}
                className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                  contactEditMode === "edit_current"
                    ? "bg-background text-foreground shadow-2xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Edit Current
              </button>
            )}
            <button
              type="button"
              onClick={() => setContactEditMode("switch_contact")}
              className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                contactEditMode === "switch_contact"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Choose Existing
            </button>
            <button
              type="button"
              onClick={() => {
                setContactEditMode("create_new");
                setContactForm({
                  first_name: "",
                  last_name: "",
                  job_title: "",
                  email: "",
                  phone: "",
                  mobile: "",
                });
              }}
              className={`flex-1 min-w-[95px] py-2 px-2 rounded-lg text-xs font-semibold text-center truncate transition-all ${
                contactEditMode === "create_new"
                  ? "bg-background text-foreground shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Create New
            </button>
          </div>

          <form onSubmit={handleContactSubmit} className="space-y-3.5 py-1">
            {contactEditMode === "switch_contact" ? (
              <div className="space-y-1.5 py-1">
                <Label className="text-xs sm:text-sm font-medium">Select Contact Person *</Label>
                <SearchSelect
                  endpoint="/api/Contact/List"
                  value={selectedContactId}
                  selectedLabel={
                    selectedContactId === deal?.primary_contact_id && deal?.primary_contact
                      ? `${deal.primary_contact.first_name || ""} ${deal.primary_contact.last_name || ""}`.trim()
                      : undefined
                  }
                  getLabel={(row) =>
                    `${`${row.first_name || ""} ${row.last_name || ""}`.trim()}${row.job_title ? ` (${row.job_title})` : ""}${row.email ? ` • ${row.email}` : ""}`
                  }
                  placeholder="Search contacts"
                  onChange={(id) => setSelectedContactId(id)}
                />
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  Links this deal to an existing contact from the CRM directory.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">First Name *</Label>
                    <Input
                      required
                      placeholder="e.g. John"
                      value={contactForm.first_name}
                      onChange={(e) => setContactForm((p) => ({ ...p, first_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Last Name</Label>
                    <Input
                      placeholder="e.g. Doe"
                      value={contactForm.last_name}
                      onChange={(e) => setContactForm((p) => ({ ...p, last_name: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Job Title / Designation</Label>
                  <Input
                    placeholder="e.g. VP of Sales, Procurement Head"
                    value={contactForm.job_title}
                    onChange={(e) => setContactForm((p) => ({ ...p, job_title: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-medium">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Phone (Direct)</Label>
                    <Input
                      placeholder="Office phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">Mobile Number</Label>
                    <Input
                      placeholder="Mobile number"
                      value={contactForm.mobile}
                      onChange={(e) => setContactForm((p) => ({ ...p, mobile: e.target.value }))}
                    />
                  </div>
                </div>
                {deal.account?.name && (
                  <p className="text-[11px] sm:text-xs text-muted-foreground flex items-center gap-1 pt-1">
                    <Building className="size-3 text-primary shrink-0" />
                    <span>Associated with {deal.account.name}</span>
                  </p>
                )}
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditContactOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  actionLoading ||
                  (contactEditMode === "switch_contact" && !selectedContactId) ||
                  (contactEditMode !== "switch_contact" && !contactForm.first_name.trim())
                }
              >
                {actionLoading ? (
                  <Loader2 className="size-4 animate-spin mr-1.5" />
                ) : null}
                {contactEditMode === "edit_current"
                  ? "Save Details"
                  : contactEditMode === "switch_contact"
                  ? "Link Contact"
                  : "Create & Link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </DealGatesProvider>
  );
}
